import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import {
  getCookieConsent,
  setCookieConsent,
  hasCookieConsentChoice,
  getDefaultConsent,
  type CookieConsent,
} from '@/utils/cookieConsent';
import { useSettings } from '@/contexts/SettingsContext';
import './CookieConsentBanner.scss';

const COOKIE_PREFERENCES_HASH = '#cookie-preferences';

export const CookieConsentBanner: React.FC = () => {
  const { getPrimaryColor } = useSettings();
  const primaryColor = getPrimaryColor();
  const location = useLocation();
  const [consent, setConsentState] = useState<CookieConsent | null>(() => getCookieConsent());
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  const openBanner = useCallback((preferencesMode: boolean) => {
    setShowBanner(true);
    setShowPreferences(preferencesMode);
  }, []);

  const closeBanner = useCallback(() => {
    setShowBanner(false);
    setShowPreferences(false);
    if (typeof window !== 'undefined' && window.location.hash === COOKIE_PREFERENCES_HASH) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  const runCheck = useCallback(() => {
    const hasChoice = hasCookieConsentChoice();
    const isPreferencesHash = window.location.hash === COOKIE_PREFERENCES_HASH;
    if (!hasChoice) {
      openBanner(isPreferencesHash);
    } else if (isPreferencesHash) {
      setConsentState(getCookieConsent());
      openBanner(true);
    } else {
      setShowBanner(false);
    }
  }, [openBanner]);

  useEffect(() => {
    runCheck();
    window.addEventListener('hashchange', runCheck);
    return () => window.removeEventListener('hashchange', runCheck);
  }, [runCheck]);

  useEffect(() => {
    if (location.hash === COOKIE_PREFERENCES_HASH) {
      runCheck();
    }
  }, [location.hash, runCheck]);

  const handleAcceptAll = () => {
    setCookieConsent({
      ...getDefaultConsent(),
      analytics: true,
      marketing: true,
    });
    setConsentState(getCookieConsent());
    closeBanner();
  };

  const handleEssentialOnly = () => {
    setCookieConsent(getDefaultConsent());
    setConsentState(getCookieConsent());
    closeBanner();
  };

  const handleSavePreferences = (prefs: { analytics: boolean; marketing: boolean }) => {
    setCookieConsent({
      essential: true,
      analytics: prefs.analytics,
      marketing: prefs.marketing,
      updatedAt: new Date().toISOString(),
    });
    setConsentState(getCookieConsent());
    closeBanner();
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        id="cookie-preferences"
        ref={bannerRef}
        className="cookie-consent-banner"
        role="dialog"
        aria-label="Cookie consent"
        aria-describedby="cookie-consent-description"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
        style={{ '--cookie-consent-primary': primaryColor } as React.CSSProperties}
      >
        <div className="cookie-consent-banner__inner">
          <div className="cookie-consent-banner__content">
            <div className="cookie-consent-banner__icon">
              <Cookie size={28} aria-hidden />
            </div>
            <div>
              <h3 className="cookie-consent-banner__title">We use cookies</h3>
              <p id="cookie-consent-description" className="cookie-consent-banner__text">
                We use essential cookies for the site to work. We’d also like to set optional
                analytics and marketing cookies to improve your experience. You can choose which
                cookies you allow. See our{' '}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="cookie-consent-banner__link" onClick={() => closeBanner()}>
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </div>

          {!showPreferences ? (
            <div className="cookie-consent-banner__actions">
              <button
                type="button"
                className="cookie-consent-banner__btn cookie-consent-banner__btn--secondary"
                onClick={() => setShowPreferences(true)}
                aria-expanded="false"
                aria-controls="cookie-preferences-panel"
              >
                Manage preferences
              </button>
              <button
                type="button"
                className="cookie-consent-banner__btn cookie-consent-banner__btn--secondary"
                onClick={handleEssentialOnly}
              >
                Essential only
              </button>
              <button
                type="button"
                className="cookie-consent-banner__btn cookie-consent-banner__btn--primary"
                onClick={handleAcceptAll}
              >
                Accept all
              </button>
            </div>
          ) : (
            <CookiePreferencesPanel
              initialAnalytics={consent?.analytics ?? false}
              initialMarketing={consent?.marketing ?? false}
              onSave={handleSavePreferences}
              onCancel={() => {
                if (hasCookieConsentChoice()) closeBanner();
                else setShowPreferences(false);
              }}
              primaryColor={primaryColor}
            />
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

interface CookiePreferencesPanelProps {
  initialAnalytics: boolean;
  initialMarketing: boolean;
  onSave: (prefs: { analytics: boolean; marketing: boolean }) => void;
  onCancel: () => void;
  primaryColor: string;
}

const CookiePreferencesPanel: React.FC<CookiePreferencesPanelProps> = ({
  initialAnalytics,
  initialMarketing,
  onSave,
  onCancel,
  primaryColor,
}) => {
  const [analytics, setAnalytics] = useState(initialAnalytics);
  const [marketing, setMarketing] = useState(initialMarketing);
  const [expanded, setExpanded] = useState(true);

  return (
    <div id="cookie-preferences-panel" className="cookie-consent-banner__preferences">
      <button
        type="button"
        className="cookie-consent-banner__preferences-toggle"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-controls="cookie-preferences-content"
      >
        <Shield size={18} aria-hidden />
        <span>Cookie preferences</span>
        {expanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
      </button>
      <div id="cookie-preferences-content" hidden={!expanded} className="cookie-consent-banner__preferences-content">
        <div className="cookie-consent-banner__toggles">
          <label className="cookie-consent-banner__toggle">
            <span className="cookie-consent-banner__toggle-label">
              <strong>Essential</strong> — Required for the site to work (e.g. security, login).
            </span>
            <input type="checkbox" checked disabled aria-label="Essential cookies (always on)" />
          </label>
          <label className="cookie-consent-banner__toggle">
            <span className="cookie-consent-banner__toggle-label">
              <strong>Analytics</strong> — Help us understand how visitors use the site.
            </span>
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              aria-label="Allow analytics cookies"
            />
          </label>
          <label className="cookie-consent-banner__toggle">
            <span className="cookie-consent-banner__toggle-label">
              <strong>Marketing</strong> — Used for relevant ads and campaigns.
            </span>
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              aria-label="Allow marketing cookies"
            />
          </label>
        </div>
        <div className="cookie-consent-banner__actions">
          <button
            type="button"
            className="cookie-consent-banner__btn cookie-consent-banner__btn--secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cookie-consent-banner__btn cookie-consent-banner__btn--primary"
            onClick={() => onSave({ analytics, marketing })}
            style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
};
